import { createContext, useState, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';

// Crear el contexto
const NotificationContext = createContext();

// Proveedor del contexto
export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  // Generar un ID único para cada notificación
  const generateId = () => {
    return Date.now().toString(36) + Math.random().toString(36).substring(2);
  };

  // Eliminar una notificación
  const removeNotification = useCallback((id) => {
    setNotifications((prevNotifications) =>
      prevNotifications.filter((notification) => notification.id !== id),
    );
  }, []);

  // Añadir una notificación
  const addNotification = useCallback(
    (notification) => {
      const id = notification.id || generateId();
      const newNotification = {
        id,
        type: notification.type || 'info',
        message: notification.message,
        title: notification.title || getDefaultTitle(notification.type),
        duration: notification.duration || 5000, // 5 segundos por defecto
        timestamp: new Date(),
      };

      setNotifications((prevNotifications) => [...prevNotifications, newNotification]);

      // Eliminar automáticamente después de la duración especificada
      if (newNotification.duration > 0) {
        setTimeout(() => {
          removeNotification(id);
        }, newNotification.duration);
      }

      return id;
    },
    [removeNotification],
  );

  // Eliminar todas las notificaciones
  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  // Mostrar una notificación de éxito
  const showSuccess = useCallback(
    (message, options = {}) => {
      return addNotification({
        type: 'success',
        message,
        ...options,
      });
    },
    [addNotification],
  );

  // Mostrar una notificación de error
  const showError = useCallback(
    (message, options = {}) => {
      return addNotification({
        type: 'error',
        message,
        ...options,
      });
    },
    [addNotification],
  );

  // Mostrar una notificación de información
  const showInfo = useCallback(
    (message, options = {}) => {
      return addNotification({
        type: 'info',
        message,
        ...options,
      });
    },
    [addNotification],
  );

  // Mostrar una notificación de advertencia
  const showWarning = useCallback(
    (message, options = {}) => {
      return addNotification({
        type: 'warning',
        message,
        ...options,
      });
    },
    [addNotification],
  );

  // Obtener el título predeterminado según el tipo de notificación
  const getDefaultTitle = (type) => {
    switch (type) {
      case 'success':
        return 'Éxito';
      case 'error':
        return 'Error';
      case 'warning':
        return 'Advertencia';
      case 'info':
      default:
        return 'Información';
    }
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        addNotification,
        removeNotification,
        clearNotifications,
        showSuccess,
        showError,
        showInfo,
        showWarning,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

NotificationProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

// Hook personalizado para usar el contexto
export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification debe ser usado dentro de un NotificationProvider');
  }
  return context;
};

export default NotificationContext;
